/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 *
 * Static CMS page content for all storefront informational pages.
 * Content is defined here per locale to avoid polluting the main i18n JSON files
 * with large multi-paragraph text blocks.
 * Supported locales: en-US, fr-FR, es-ES, it-IT, de-DE
 */

export type CmsSection = {
  heading: string;
  body: string;
};

export type CmsPageContent = {
  title: string;
  lead: string;
  sections: CmsSection[];
};

/** Map: handle → locale → content */
export const CMS_CONTENT: Record<string, Record<string, CmsPageContent>> = {
  // ─────────────────────────── ABOUT ──────────────────────────────────────
  about: {
    "en-US": {
      title: "About Cantica Divina",
      lead: "Cantica Divina is a metal band forging Dante's Divine Comedy into sound. This is our official store — every design is created by the band and printed on demand.",
      sections: [
        {
          heading: "The Band",
          body: "Cantica Divina turns the cantos of Dante's Divine Comedy into modern metal. Our first chapter, Inferno — The Song of the Abyss, descends circle after circle into the darkness of the human soul.",
        },
        {
          heading: "The Merch",
          body: "Every item in this store is designed by the band and produced on demand by our print partner. No stockpiles, no waste — each piece is made when you order it.",
        },
        {
          heading: "Independent",
          body: "We are independent and self-produced. Every purchase directly supports the band and fuels the next descent. Questions? Write to store@canticadivina.com.",
        },
      ],
    },
    "fr-FR": {
      title: "À propos de Cantica Divina",
      lead: "Cantica Divina est un groupe de metal qui forge la Divine Comédie de Dante en musique. Voici notre boutique officielle — chaque visuel est créé par le groupe et imprimé à la demande.",
      sections: [
        {
          heading: "Le Groupe",
          body: "Cantica Divina transforme les chants de la Divine Comédie de Dante en metal moderne. Notre premier chapitre, Inferno — The Song of the Abyss, descend cercle après cercle dans les ténèbres de l'âme humaine.",
        },
        {
          heading: "Le Merch",
          body: "Chaque article de cette boutique est conçu par le groupe et produit à la demande par notre partenaire d'impression. Pas de stocks, pas de gaspillage.",
        },
        {
          heading: "Indépendants",
          body: "Nous sommes indépendants et autoproduits. Chaque achat soutient directement le groupe. Une question ? Écrivez à store@canticadivina.com.",
        },
      ],
    },
    "es-ES": {
      title: "Acerca de Cantica Divina",
      lead: "Cantica Divina es una banda de metal que forja la Divina Comedia de Dante en sonido. Esta es nuestra tienda oficial: cada diseño es creado por la banda e impreso bajo demanda.",
      sections: [
        {
          heading: "La Banda",
          body: "Cantica Divina convierte los cantos de la Divina Comedia de Dante en metal moderno. Nuestro primer capítulo, Inferno — The Song of the Abyss, desciende círculo a círculo en la oscuridad del alma humana.",
        },
        {
          heading: "El Merch",
          body: "Cada artículo de esta tienda está diseñado por la banda y producido bajo demanda por nuestro socio de impresión. Sin stocks, sin desperdicio.",
        },
        {
          heading: "Independientes",
          body: "Somos independientes y autoproducidos. Cada compra apoya directamente a la banda. ¿Preguntas? Escribe a store@canticadivina.com.",
        },
      ],
    },
    "it-IT": {
      title: "Chi è Cantica Divina",
      lead: "Cantica Divina è una band metal che trasforma la Divina Commedia di Dante in musica. Questo è il nostro store ufficiale — ogni grafica è creata dalla band e stampata su richiesta.",
      sections: [
        {
          heading: "La Band",
          body: "Cantica Divina trasforma i canti della Divina Commedia di Dante in metal moderno. Il nostro primo capitolo, Inferno — The Song of the Abyss, scende cerchio dopo cerchio nell'oscurità dell'animo umano.",
        },
        {
          heading: "Il Merch",
          body: "Ogni articolo di questo store è disegnato dalla band e prodotto su richiesta dal nostro partner di stampa. Nessuna scorta, nessuno spreco: ogni pezzo viene realizzato quando lo ordini.",
        },
        {
          heading: "Indipendenti",
          body: "Siamo indipendenti e autoprodotti. Ogni acquisto sostiene direttamente la band e alimenta la prossima discesa. Domande? Scrivi a store@canticadivina.com.",
        },
      ],
    },
    "de-DE": {
      title: "Über Cantica Divina",
      lead: "Cantica Divina ist eine Metal-Band, die Dantes Göttliche Komödie in Klang schmiedet. Dies ist unser offizieller Store — jedes Design wird von der Band erstellt und auf Bestellung gedruckt.",
      sections: [
        {
          heading: "Die Band",
          body: "Cantica Divina verwandelt die Gesänge von Dantes Göttlicher Komödie in modernen Metal. Unser erstes Kapitel, Inferno — The Song of the Abyss, steigt Kreis für Kreis in die Dunkelheit der menschlichen Seele hinab.",
        },
        {
          heading: "Das Merch",
          body: "Jedes Produkt in diesem Store wird von der Band entworfen und auf Bestellung von unserem Druckpartner produziert. Keine Lagerbestände, keine Verschwendung — jedes Stück wird erst bei Bestellung hergestellt.",
        },
        {
          heading: "Unabhängig",
          body: "Wir sind unabhängig und selbstproduziert. Jeder Kauf unterstützt die Band direkt und treibt den nächsten Abstieg voran. Fragen? Schreib an store@canticadivina.com.",
        },
      ],
    },
  },

  // ─────────────────────────── BLOG ───────────────────────────────────────
  blog: {
    "en-US": {
      title: "Cantica Divina Blog",
      lead: "Discover insights on e-commerce, platform updates, merchant success stories, and technical deep-dives from the Cantica Divina team.",
      sections: [
        {
          heading: "Coming Soon",
          body: "Our blog is under construction. We are preparing in-depth articles on building performant e-commerce storefronts, leveraging Cloudflare's edge for global reach, and practical tips for growing your online business.",
        },
        {
          heading: "What to Expect",
          body: "From tutorials on Cloudflare Workers and Durable Objects, to case studies of merchants who scaled their business with Cantica Divina — our blog will cover topics that matter to developers and shop owners alike.",
        },
        {
          heading: "Stay in the Loop",
          body: "Subscribe to our newsletter at the bottom of this page to be the first notified when new articles go live. We publish new content every two weeks.",
        },
      ],
    },
    "fr-FR": {
      title: "Blog Cantica Divina",
      lead: "Découvrez des articles sur le e-commerce, les mises à jour de la plateforme, les success stories de marchands et les analyses techniques de l'équipe Cantica Divina.",
      sections: [
        {
          heading: "Bientôt disponible",
          body: "Notre blog est en cours de construction. Nous préparons des articles approfondis sur la création de storefronts e-commerce performants, l'exploitation du réseau edge de Cloudflare et des conseils pratiques pour développer votre activité en ligne.",
        },
        {
          heading: "Ce qui vous attend",
          body: "Des tutoriels sur Cloudflare Workers et Durable Objects aux études de cas de marchands ayant développé leur activité avec Cantica Divina — notre blog abordera des sujets qui comptent pour les développeurs comme pour les propriétaires de boutiques.",
        },
        {
          heading: "Restez informé",
          body: "Abonnez-vous à notre newsletter en bas de cette page pour être informé en premier lorsque de nouveaux articles sont publiés. Nous publions du contenu toutes les deux semaines.",
        },
      ],
    },
    "es-ES": {
      title: "Blog de Cantica Divina",
      lead: "Descubre artículos sobre e-commerce, actualizaciones de la plataforma, historias de éxito de comerciantes y análisis técnicos del equipo de Cantica Divina.",
      sections: [
        {
          heading: "Próximamente",
          body: "Nuestro blog está en construcción. Estamos preparando artículos detallados sobre cómo construir storefronts de e-commerce de alto rendimiento, aprovechar el edge de Cloudflare y consejos prácticos para hacer crecer tu negocio en línea.",
        },
        {
          heading: "Qué esperar",
          body: "Desde tutoriales sobre Cloudflare Workers hasta casos de estudio de comerciantes que escalaron su negocio con Cantica Divina — nuestro blog cubrirá temas que importan tanto a desarrolladores como a propietarios de tiendas.",
        },
        {
          heading: "Mantente informado",
          body: "Suscríbete a nuestro boletín al final de esta página para ser el primero en recibir notificaciones cuando se publiquen nuevos artículos. Publicamos contenido cada dos semanas.",
        },
      ],
    },
    "it-IT": {
      title: "Blog di Cantica Divina",
      lead: "Scopri approfondimenti su e-commerce, aggiornamenti della piattaforma, storie di successo dei merchant e analisi tecniche dal team Cantica Divina.",
      sections: [
        {
          heading: "In arrivo",
          body: "Il nostro blog è in costruzione. Stiamo preparando articoli approfonditi sulla creazione di storefront e-commerce performanti, sfruttando l'edge di Cloudflare per una copertura globale, e consigli pratici per far crescere la tua attività online.",
        },
        {
          heading: "Cosa aspettarsi",
          body: "Da tutorial su Cloudflare Workers e Durable Objects, a casi studio di merchant che hanno scalato il loro business con Cantica Divina — il nostro blog tratterà argomenti rilevanti sia per gli sviluppatori che per i proprietari di negozi.",
        },
        {
          heading: "Resta aggiornato",
          body: "Iscriviti alla nostra newsletter in fondo a questa pagina per essere il primo ad essere avvisato quando nuovi articoli vengono pubblicati. Pubblichiamo nuovi contenuti ogni due settimane.",
        },
      ],
    },
    "de-DE": {
      title: "Cantica Divina Blog",
      lead: "Entdecke Einblicke zu E-Commerce, Plattform-Updates, Erfolgsgeschichten von Händlern und technischen Deep-Dives des Cantica-Divina-Teams.",
      sections: [
        {
          heading: "Demnächst verfügbar",
          body: "Unser Blog befindet sich im Aufbau. Wir bereiten ausführliche Artikel über den Aufbau performanter E-Commerce-Storefronts, die Nutzung von Cloudflares Edge-Netzwerk für globale Reichweite und praktische Tipps zum Wachstum deines Online-Geschäfts vor.",
        },
        {
          heading: "Was dich erwartet",
          body: "Von Tutorials zu Cloudflare Workers und Durable Objects bis hin zu Fallstudien von Händlern, die ihr Geschäft mit Cantica Divina skaliert haben — unser Blog wird Themen behandeln, die sowohl für Entwickler als auch für Shop-Betreiber relevant sind.",
        },
        {
          heading: "Bleib auf dem Laufenden",
          body: "Abonniere unseren Newsletter am Ende dieser Seite, um als Erster benachrichtigt zu werden, wenn neue Artikel veröffentlicht werden. Wir veröffentlichen alle zwei Wochen neue Inhalte.",
        },
      ],
    },
  },

  // ─────────────────────────── CAREERS ────────────────────────────────────
  careers: {
    "en-US": {
      title: "Careers at Cantica Divina",
      lead: "Help us build the future of edge-native e-commerce. We're a small, focused team that values craftsmanship, autonomy, and impact.",
      sections: [
        {
          heading: "Our Culture",
          body: "We are remote-first, async-friendly, and deeply technical. We care about the quality of our code as much as the quality of the experience we deliver. Every team member has a real impact on the product, and ideas are welcomed from everyone regardless of seniority.",
        },
        {
          heading: "Open Positions",
          body: "We don't currently have any open positions, but we're always interested in meeting exceptional engineers, designers, and product thinkers. If you're passionate about edge computing, open source, or e-commerce, feel free to reach out at store@canticadivina.com.",
        },
        {
          heading: "What We Offer",
          body: "Competitive compensation, equity participation, full remote flexibility, a generous equipment budget, and the opportunity to work on open-source software used by merchants worldwide. We take work-life balance seriously.",
        },
      ],
    },
    "fr-FR": {
      title: "Carrières chez Cantica Divina",
      lead: "Aidez-nous à construire l'avenir du e-commerce natif en bordure de réseau. Nous sommes une équipe restreinte et concentrée qui valorise l'artisanat, l'autonomie et l'impact.",
      sections: [
        {
          heading: "Notre culture",
          body: "Nous sommes remote-first, async-friendly et profondément techniques. Nous accordons autant d'importance à la qualité de notre code qu'à la qualité de l'expérience que nous offrons. Chaque membre de l'équipe a un impact réel sur le produit.",
        },
        {
          heading: "Postes ouverts",
          body: "Nous n'avons actuellement aucun poste ouvert, mais nous sommes toujours intéressés par des ingénieurs, designers et product thinkers exceptionnels. Si vous êtes passionné par le edge computing, l'open source ou le e-commerce, contactez-nous à store@canticadivina.com.",
        },
        {
          heading: "Ce que nous offrons",
          body: "Rémunération compétitive, participation au capital, flexibilité full remote, budget équipement généreux et opportunité de travailler sur un logiciel open source utilisé par des marchands dans le monde entier.",
        },
      ],
    },
    "es-ES": {
      title: "Carreras en Cantica Divina",
      lead: "Ayúdanos a construir el futuro del e-commerce nativo en el edge. Somos un equipo pequeño y enfocado que valora el artesanado, la autonomía y el impacto.",
      sections: [
        {
          heading: "Nuestra cultura",
          body: "Somos remote-first, async-friendly y profundamente técnicos. Nos importa tanto la calidad de nuestro código como la calidad de la experiencia que ofrecemos. Cada miembro del equipo tiene un impacto real en el producto.",
        },
        {
          heading: "Posiciones abiertas",
          body: "Actualmente no tenemos posiciones abiertas, pero siempre estamos interesados en conocer ingenieros, diseñadores y pensadores de producto excepcionales. Si te apasiona la computación en el edge, el código abierto o el e-commerce, contáctanos en store@canticadivina.com.",
        },
        {
          heading: "Lo que ofrecemos",
          body: "Compensación competitiva, participación en equity, flexibilidad total remota, presupuesto generoso para equipo y la oportunidad de trabajar en software open source utilizado por comerciantes en todo el mundo.",
        },
      ],
    },
    "it-IT": {
      title: "Lavora con noi — Cantica Divina",
      lead: "Aiutaci a costruire il futuro dell'e-commerce edge-native. Siamo un team piccolo e concentrato che valorizza cura artigianale, autonomia e impatto.",
      sections: [
        {
          heading: "La nostra cultura",
          body: "Siamo remote-first, async-friendly e profondamente tecnici. Ci importa la qualità del nostro codice tanto quanto la qualità dell'esperienza che offriamo. Ogni membro del team ha un impatto reale sul prodotto, e le idee sono benvenute da chiunque, indipendentemente dall'anzianità.",
        },
        {
          heading: "Posizioni aperte",
          body: "Al momento non abbiamo posizioni aperte, ma siamo sempre interessati a conoscere ingegneri, designer e product thinker eccezionali. Se sei appassionato di edge computing, open source o e-commerce, scrivici a store@canticadivina.com.",
        },
        {
          heading: "Cosa offriamo",
          body: "Retribuzione competitiva, partecipazione azionaria, piena flessibilità da remoto, un generoso budget per l'attrezzatura e l'opportunità di lavorare su software open source usato da merchant in tutto il mondo. Prendiamo sul serio l'equilibrio vita-lavoro.",
        },
      ],
    },
    "de-DE": {
      title: "Karriere bei Cantica Divina",
      lead: "Hilf uns, die Zukunft des edge-nativen E-Commerce zu gestalten. Wir sind ein kleines, fokussiertes Team, das Handwerkskunst, Autonomie und Wirkung schätzt.",
      sections: [
        {
          heading: "Unsere Kultur",
          body: "Wir arbeiten remote-first, sind async-freundlich und tief technisch. Uns liegt die Qualität unseres Codes genauso am Herzen wie die Qualität der Erfahrung, die wir liefern. Jedes Teammitglied hat echten Einfluss auf das Produkt, und Ideen sind von allen willkommen, unabhängig vom Senioritätslevel.",
        },
        {
          heading: "Offene Stellen",
          body: "Derzeit haben wir keine offenen Stellen, sind aber immer daran interessiert, außergewöhnliche Ingenieure, Designer und Produktdenker kennenzulernen. Wenn du dich für Edge Computing, Open Source oder E-Commerce begeisterst, melde dich gerne unter store@canticadivina.com.",
        },
        {
          heading: "Was wir bieten",
          body: "Wettbewerbsfähige Vergütung, Beteiligung am Unternehmen, volle Remote-Flexibilität, ein großzügiges Budget für Arbeitsmittel und die Möglichkeit, an Open-Source-Software zu arbeiten, die von Händlern weltweit genutzt wird. Wir nehmen die Work-Life-Balance ernst.",
        },
      ],
    },
  },

  // ─────────────────────────── PRESS ──────────────────────────────────────
  press: {
    "en-US": {
      title: "Press & Media",
      lead: "Find the information you need to write about Cantica Divina — our story, brand assets, and press contacts all in one place.",
      sections: [
        {
          heading: "About Cantica Divina",
          body: "Cantica Divina is a next-generation open-source e-commerce platform based on Cloudflare Workers and Durable Objects. Founded in 2024, it enables merchants to deploy globally distributed stores with zero infrastructure management. The platform supports multi-language, multi-currency, and multi-region operations out of the box.",
        },
        {
          heading: "Press Kit",
          body: "Our press kit includes high-resolution logos, product screenshots, founder bios, and key company facts. Download the press kit at store@canticadivina.com or visit our GitHub repository. All assets are freely available for editorial use with attribution.",
        },
        {
          heading: "Press Contact",
          body: "For press inquiries, interview requests, and partnership announcements, please contact our communications team at store@canticadivina.com. We typically respond within one business day.",
        },
      ],
    },
    "fr-FR": {
      title: "Presse & Médias",
      lead: "Trouvez toutes les informations nécessaires pour écrire sur Cantica Divina — notre histoire, nos ressources de marque et nos contacts presse en un seul endroit.",
      sections: [
        {
          heading: "À propos de Cantica Divina",
          body: "Cantica Divina est une plateforme e-commerce open source de nouvelle génération basée sur Cloudflare Workers et Durable Objects. Fondée en 2024, elle permet aux marchands de déployer des boutiques distribuées globalement sans aucune gestion d'infrastructure. La plateforme prend en charge les opérations multilingues, multi-devises et multirégions nativement.",
        },
        {
          heading: "Kit presse",
          body: "Notre kit presse comprend des logos haute résolution, des captures d'écran du produit, des biographies des fondateurs et des faits clés sur l'entreprise. Téléchargez le kit presse depuis store@canticadivina.com ou visitez notre dépôt GitHub.",
        },
        {
          heading: "Contact presse",
          body: "Pour les demandes presse, les interviews et les annonces de partenariats, veuillez contacter notre équipe de communication à store@canticadivina.com. Nous répondons généralement dans un délai d'un jour ouvrable.",
        },
      ],
    },
    "es-ES": {
      title: "Prensa y Medios",
      lead: "Encuentra toda la información que necesitas para escribir sobre Cantica Divina — nuestra historia, recursos de marca y contactos de prensa en un solo lugar.",
      sections: [
        {
          heading: "Acerca de Cantica Divina",
          body: "Cantica Divina es una plataforma de e-commerce open source de nueva generación basada en Cloudflare Workers y Durable Objects. Fundada en 2024, permite a los comerciantes desplegar tiendas distribuidas globalmente sin ninguna gestión de infraestructura.",
        },
        {
          heading: "Kit de prensa",
          body: "Nuestro kit de prensa incluye logotipos en alta resolución, capturas de pantalla del producto, biografías de los fundadores y datos clave de la empresa. Descarga el kit de prensa desde store@canticadivina.com o visita nuestro repositorio de GitHub.",
        },
        {
          heading: "Contacto de prensa",
          body: "Para consultas de prensa, solicitudes de entrevistas y anuncios de asociaciones, contacta a nuestro equipo de comunicaciones en store@canticadivina.com. Normalmente respondemos en un día hábil.",
        },
      ],
    },
    "it-IT": {
      title: "Stampa e Media",
      lead: "Trova le informazioni di cui hai bisogno per scrivere di Cantica Divina — la nostra storia, gli asset del brand e i contatti stampa, tutto in un unico posto.",
      sections: [
        {
          heading: "Chi è Cantica Divina",
          body: "Cantica Divina è una piattaforma e-commerce open source di nuova generazione basata su Cloudflare Workers e Durable Objects. Fondata nel 2024, consente ai merchant di lanciare negozi distribuiti globalmente senza alcuna gestione dell'infrastruttura. La piattaforma supporta operazioni multilingua, multi-valuta e multi-regione fin da subito.",
        },
        {
          heading: "Press Kit",
          body: "Il nostro press kit include loghi ad alta risoluzione, screenshot del prodotto, biografie dei fondatori e dati chiave dell'azienda. Scarica il press kit scrivendo a store@canticadivina.com o visita il nostro repository GitHub. Tutti gli asset sono liberamente disponibili per uso editoriale con attribuzione.",
        },
        {
          heading: "Contatto stampa",
          body: "Per richieste stampa, interviste e annunci di partnership, contatta il nostro team di comunicazione a store@canticadivina.com. In genere rispondiamo entro un giorno lavorativo.",
        },
      ],
    },
    "de-DE": {
      title: "Presse & Medien",
      lead: "Finde alle Informationen, die du zum Schreiben über Cantica Divina benötigst — unsere Geschichte, Markenmaterial und Pressekontakte an einem Ort.",
      sections: [
        {
          heading: "Über Cantica Divina",
          body: "Cantica Divina ist eine E-Commerce-Plattform der nächsten Generation auf Open-Source-Basis, die auf Cloudflare Workers und Durable Objects aufbaut. 2024 gegründet, ermöglicht sie Händlern, global verteilte Shops ohne jegliche Infrastrukturverwaltung zu betreiben. Die Plattform unterstützt von Haus aus Mehrsprachigkeit, mehrere Währungen und mehrere Regionen.",
        },
        {
          heading: "Presse-Kit",
          body: "Unser Presse-Kit enthält hochauflösende Logos, Produkt-Screenshots, Gründerbiografien und wichtige Unternehmensfakten. Lade das Presse-Kit über store@canticadivina.com herunter oder besuche unser GitHub-Repository. Alle Materialien stehen für redaktionelle Zwecke mit Quellenangabe frei zur Verfügung.",
        },
        {
          heading: "Presse-Kontakt",
          body: "Für Presseanfragen, Interviewanfragen und Partnerschaftsankündigungen kontaktiere bitte unser Kommunikationsteam unter store@canticadivina.com. Wir antworten in der Regel innerhalb eines Werktags.",
        },
      ],
    },
  },

  // ─────────────────────────── CONTACT ────────────────────────────────────
  contact: {
    "en-US": {
      title: "Contact Us",
      lead: "We'd love to hear from you. Whether you have a question about the platform, need help with an order, or want to explore a partnership — we're here.",
      sections: [
        {
          heading: "Customer Support",
          body: "For order-related questions, shipping inquiries, and returns, reach out to our support team at store@canticadivina.com. We aim to respond to all inquiries within 24 hours on business days.",
        },
        {
          heading: "Business Inquiries",
          body: "For partnerships, integrations, and enterprise inquiries, please contact us at store@canticadivina.com. We're always open to interesting collaborations with other platforms and merchants.",
        },
        {
          heading: "Support Hours",
          body: "Our support team is available Monday to Friday, 9:00 AM – 6:00 PM (CET). During peak periods such as holiday season, we extend our hours to ensure timely responses.",
        },
      ],
    },
    "fr-FR": {
      title: "Contactez-nous",
      lead: "Nous serions ravis d'avoir de vos nouvelles. Que vous ayez une question sur la plateforme, besoin d'aide pour une commande, ou souhaitiez explorer un partenariat — nous sommes là.",
      sections: [
        {
          heading: "Support client",
          body: "Pour les questions relatives aux commandes, aux expéditions et aux retours, contactez notre équipe de support à store@canticadivina.com. Nous nous engageons à répondre à toutes les demandes dans les 24 heures les jours ouvrables.",
        },
        {
          heading: "Demandes commerciales",
          body: "Pour les partenariats, intégrations et demandes entreprises, veuillez nous contacter à store@canticadivina.com. Nous sommes toujours ouverts à des collaborations intéressantes avec d'autres plateformes et marchands.",
        },
        {
          heading: "Horaires du support",
          body: "Notre équipe de support est disponible du lundi au vendredi de 9h00 à 18h00 (CET). Pendant les périodes de pointe comme les fêtes, nous prolongeons nos horaires pour garantir des réponses rapides.",
        },
      ],
    },
    "es-ES": {
      title: "Contáctanos",
      lead: "Nos encantaría saber de ti. Ya sea que tengas una pregunta sobre la plataforma, necesites ayuda con un pedido, o quieras explorar una colaboración — estamos aquí.",
      sections: [
        {
          heading: "Soporte al cliente",
          body: "Para preguntas relacionadas con pedidos, envíos y devoluciones, contacta a nuestro equipo de soporte en store@canticadivina.com. Nos comprometemos a responder todas las consultas en 24 horas en días hábiles.",
        },
        {
          heading: "Consultas de negocio",
          body: "Para asociaciones, integraciones y consultas empresariales, contáctanos en store@canticadivina.com. Siempre estamos abiertos a colaboraciones interesantes con otras plataformas y comerciantes.",
        },
        {
          heading: "Horario de soporte",
          body: "Nuestro equipo de soporte está disponible de lunes a viernes, de 9:00 a 18:00 (CET). Durante períodos de alta demanda como la temporada navideña, ampliamos nuestros horarios.",
        },
      ],
    },
    "it-IT": {
      title: "Contattaci",
      lead: "Ci farebbe piacere sentirti. Che tu abbia una domanda sulla piattaforma, bisogno di aiuto con un ordine, o voglia esplorare una partnership — siamo qui.",
      sections: [
        {
          heading: "Assistenza clienti",
          body: "Per domande relative agli ordini, richieste sulla spedizione e resi, contatta il nostro team di assistenza a store@canticadivina.com. Puntiamo a rispondere a tutte le richieste entro 24 ore nei giorni lavorativi.",
        },
        {
          heading: "Richieste commerciali",
          body: "Per partnership, integrazioni e richieste enterprise, contattaci a store@canticadivina.com. Siamo sempre aperti a collaborazioni interessanti con altre piattaforme e merchant.",
        },
        {
          heading: "Orari di assistenza",
          body: "Il nostro team di assistenza è disponibile dal lunedì al venerdì, dalle 9:00 alle 18:00 (CET). Durante i periodi di punta, come la stagione delle feste, estendiamo i nostri orari per garantire risposte tempestive.",
        },
      ],
    },
    "de-DE": {
      title: "Kontaktiere uns",
      lead: "Wir würden gerne von dir hören. Ob du eine Frage zur Plattform hast, Hilfe bei einer Bestellung brauchst oder eine Partnerschaft erkunden möchtest — wir sind hier.",
      sections: [
        {
          heading: "Kundensupport",
          body: "Für Fragen zu Bestellungen, Versandanfragen und Rücksendungen wende dich an unser Support-Team unter store@canticadivina.com. Wir bemühen uns, an Werktagen innerhalb von 24 Stunden auf alle Anfragen zu antworten.",
        },
        {
          heading: "Geschäftsanfragen",
          body: "Für Partnerschaften, Integrationen und Unternehmensanfragen kontaktiere uns bitte unter store@canticadivina.com. Wir sind immer offen für interessante Kooperationen mit anderen Plattformen und Händlern.",
        },
        {
          heading: "Support-Zeiten",
          body: "Unser Support-Team ist von Montag bis Freitag, 9:00–18:00 Uhr (MEZ) erreichbar. In Spitzenzeiten wie der Feiertagssaison verlängern wir unsere Öffnungszeiten, um zeitnahe Antworten sicherzustellen.",
        },
      ],
    },
  },

  // ─────────────────────────── FAQ ────────────────────────────────────────
  faq: {
    "en-US": {
      title: "Frequently Asked Questions",
      lead: "Everything you need to know about shopping with us — orders, payments, shipping, and returns.",
      sections: [
        {
          heading: "Shipping & Delivery",
          body: "Standard delivery takes 3–5 business days. Express delivery (1–2 business days) is available at checkout. Free shipping applies to orders over €50. International orders may take 7–14 business days depending on the destination country and local customs clearance.",
        },
        {
          heading: "Returns & Refunds",
          body: "We accept returns within 30 days of delivery, provided items are unused and in original packaging. To initiate a return, log into your account and visit the orders section. Refunds are processed within 5–7 business days after we receive the returned item.",
        },
        {
          heading: "Payments",
          body: "We accept Visa, Mastercard, PayPal, Stripe, and Apple Pay. All transactions are secured with TLS encryption. We do not store card information — payments are handled entirely by our certified payment processors.",
        },
      ],
    },
    "fr-FR": {
      title: "Questions Fréquentes",
      lead: "Tout ce que vous devez savoir sur vos achats — commandes, paiements, livraisons et retours.",
      sections: [
        {
          heading: "Livraison et expédition",
          body: "La livraison standard prend 3 à 5 jours ouvrables. La livraison express (1 à 2 jours ouvrables) est disponible lors du passage en caisse. La livraison gratuite s'applique aux commandes supérieures à 50 €. Les commandes internationales peuvent prendre 7 à 14 jours ouvrables selon le pays de destination.",
        },
        {
          heading: "Retours et remboursements",
          body: "Nous acceptons les retours dans les 30 jours suivant la livraison, à condition que les articles soient inutilisés et dans leur emballage d'origine. Pour initier un retour, connectez-vous à votre compte et consultez la section commandes. Les remboursements sont traités dans un délai de 5 à 7 jours ouvrables.",
        },
        {
          heading: "Paiements",
          body: "Nous acceptons Visa, Mastercard, PayPal, Stripe et Apple Pay. Toutes les transactions sont sécurisées par chiffrement TLS. Nous ne stockons pas les informations de carte — les paiements sont entièrement gérés par nos processeurs de paiement certifiés.",
        },
      ],
    },
    "es-ES": {
      title: "Preguntas Frecuentes",
      lead: "Todo lo que necesitas saber sobre tus compras — pedidos, pagos, envíos y devoluciones.",
      sections: [
        {
          heading: "Envío y entrega",
          body: "El envío estándar tarda de 3 a 5 días hábiles. El envío express (1-2 días hábiles) está disponible al finalizar la compra. El envío gratuito aplica a pedidos superiores a 50 €. Los pedidos internacionales pueden tardar de 7 a 14 días hábiles.",
        },
        {
          heading: "Devoluciones y reembolsos",
          body: "Aceptamos devoluciones dentro de los 30 días posteriores a la entrega, siempre que los artículos estén sin usar y en su embalaje original. Para iniciar una devolución, inicia sesión en tu cuenta y visita la sección de pedidos. Los reembolsos se procesan en 5-7 días hábiles.",
        },
        {
          heading: "Pagos",
          body: "Aceptamos Visa, Mastercard, PayPal, Stripe y Apple Pay. Todas las transacciones están protegidas con encriptación TLS. No almacenamos información de tarjetas — los pagos son gestionados enteramente por nuestros procesadores de pago certificados.",
        },
      ],
    },
    "it-IT": {
      title: "Domande Frequenti",
      lead: "Tutto ciò che devi sapere sui tuoi acquisti — ordini, pagamenti, spedizioni e resi.",
      sections: [
        {
          heading: "Spedizione e consegna",
          body: "La consegna standard richiede 3-5 giorni lavorativi. La consegna express (1-2 giorni lavorativi) è disponibile al checkout. La spedizione gratuita si applica agli ordini superiori a 50 €. Gli ordini internazionali possono richiedere 7-14 giorni lavorativi a seconda del paese di destinazione e dello sdoganamento locale.",
        },
        {
          heading: "Resi e rimborsi",
          body: "Accettiamo resi entro 30 giorni dalla consegna, a condizione che gli articoli siano inutilizzati e nella confezione originale. Per avviare un reso, accedi al tuo account e visita la sezione ordini. I rimborsi vengono elaborati entro 5-7 giorni lavorativi dal ricevimento dell'articolo restituito.",
        },
        {
          heading: "Pagamenti",
          body: "Accettiamo Visa, Mastercard, PayPal, Stripe e Apple Pay. Tutte le transazioni sono protette con crittografia TLS. Non memorizziamo i dati della carta: i pagamenti sono gestiti interamente dai nostri processori di pagamento certificati.",
        },
      ],
    },
    "de-DE": {
      title: "Häufig gestellte Fragen",
      lead: "Alles, was du über das Einkaufen bei uns wissen musst — Bestellungen, Zahlungen, Versand und Rücksendungen.",
      sections: [
        {
          heading: "Versand & Lieferung",
          body: "Der Standardversand dauert 3–5 Werktage. Expressversand (1–2 Werktage) ist an der Kasse verfügbar. Kostenloser Versand gilt für Bestellungen über 50 €. Internationale Bestellungen können je nach Zielland und Zollabfertigung 7–14 Werktage dauern.",
        },
        {
          heading: "Rücksendungen & Erstattungen",
          body: "Wir akzeptieren Rücksendungen innerhalb von 30 Tagen nach Lieferung, sofern die Artikel unbenutzt und in der Originalverpackung sind. Um eine Rücksendung zu starten, melde dich in deinem Konto an und besuche den Bestellbereich. Erstattungen werden innerhalb von 5–7 Werktagen nach Erhalt des zurückgesendeten Artikels bearbeitet.",
        },
        {
          heading: "Zahlungen",
          body: "Wir akzeptieren Visa, Mastercard, PayPal, Stripe und Apple Pay. Alle Transaktionen sind mit TLS-Verschlüsselung gesichert. Wir speichern keine Kartendaten — Zahlungen werden vollständig von unseren zertifizierten Zahlungsdienstleistern abgewickelt.",
        },
      ],
    },
  },

  // ─────────────────────────── SHIPPING POLICY ────────────────────────────
  "shipping-policy": {
    "en-US": {
      title: "Shipping Policy",
      lead: "We are committed to delivering your orders quickly, safely, and at a fair price. Here is everything you need to know about how we ship.",
      sections: [
        {
          heading: "Order Processing",
          body: "Orders are processed within 1–2 business days of payment confirmation. Orders placed on weekends or public holidays are processed on the next business day. You will receive a confirmation email with tracking information once your order ships.",
        },
        {
          heading: "Delivery Methods & Rates",
          body: "Standard shipping (3–5 business days) is free on orders over €50, and €4.99 for smaller orders. Express shipping (1–2 business days) is available for €9.99. Same-day delivery is offered in selected cities at a surcharge.",
        },
        {
          heading: "International Shipping",
          body: "We ship to over 50 countries worldwide. International delivery typically takes 7–14 business days. Import duties and taxes may apply upon arrival and are the responsibility of the recipient. We are not liable for delays caused by customs.",
        },
      ],
    },
    "fr-FR": {
      title: "Politique de livraison",
      lead: "Nous nous engageons à livrer vos commandes rapidement, en toute sécurité et à un prix équitable. Voici tout ce que vous devez savoir sur notre mode d'expédition.",
      sections: [
        {
          heading: "Traitement des commandes",
          body: "Les commandes sont traitées dans un délai de 1 à 2 jours ouvrables suivant la confirmation du paiement. Les commandes passées les week-ends ou les jours fériés sont traitées le prochain jour ouvrable. Vous recevrez un e-mail de confirmation avec les informations de suivi dès l'expédition.",
        },
        {
          heading: "Modes de livraison et tarifs",
          body: "La livraison standard (3 à 5 jours ouvrables) est gratuite pour les commandes supérieures à 50 € et coûte 4,99 € pour les commandes moins importantes. La livraison express (1 à 2 jours ouvrables) est disponible à 9,99 €.",
        },
        {
          heading: "Livraison internationale",
          body: "Nous livrons dans plus de 50 pays dans le monde. La livraison internationale prend généralement 7 à 14 jours ouvrables. Des droits de douane et taxes peuvent s'appliquer à l'arrivée et sont à la charge du destinataire.",
        },
      ],
    },
    "es-ES": {
      title: "Política de Envío",
      lead: "Estamos comprometidos a entregar tus pedidos de forma rápida, segura y a un precio justo. Aquí tienes todo lo que necesitas saber sobre cómo enviamos.",
      sections: [
        {
          heading: "Procesamiento de pedidos",
          body: "Los pedidos se procesan en 1-2 días hábiles tras la confirmación del pago. Los pedidos realizados en fines de semana o días festivos se procesan el siguiente día hábil. Recibirás un email de confirmación con información de seguimiento una vez enviado tu pedido.",
        },
        {
          heading: "Métodos y tarifas de envío",
          body: "El envío estándar (3-5 días hábiles) es gratuito en pedidos superiores a €50 y cuesta €4,99 para pedidos menores. El envío express (1-2 días hábiles) está disponible por €9,99.",
        },
        {
          heading: "Envío internacional",
          body: "Enviamos a más de 50 países en todo el mundo. La entrega internacional suele tardar de 7 a 14 días hábiles. Los aranceles e impuestos de importación pueden aplicarse a la llegada y son responsabilidad del destinatario.",
        },
      ],
    },
    "it-IT": {
      title: "Politica di Spedizione",
      lead: "Ci impegniamo a consegnare i tuoi ordini in modo rapido, sicuro e a un prezzo equo. Ecco tutto ciò che devi sapere su come spediamo.",
      sections: [
        {
          heading: "Elaborazione degli ordini",
          body: "Gli ordini vengono elaborati entro 1-2 giorni lavorativi dalla conferma del pagamento. Gli ordini effettuati nei fine settimana o nei giorni festivi vengono elaborati il giorno lavorativo successivo. Riceverai un'email di conferma con le informazioni di tracciamento non appena il tuo ordine viene spedito.",
        },
        {
          heading: "Metodi e tariffe di spedizione",
          body: "La spedizione standard (3-5 giorni lavorativi) è gratuita per ordini superiori a 50 € e costa 4,99 € per ordini più piccoli. La spedizione express (1-2 giorni lavorativi) è disponibile per 9,99 €. La consegna in giornata è offerta in città selezionate con un supplemento.",
        },
        {
          heading: "Spedizione internazionale",
          body: "Spediamo in oltre 50 paesi nel mondo. La consegna internazionale richiede solitamente 7-14 giorni lavorativi. Dazi doganali e tasse potrebbero essere applicati all'arrivo e sono a carico del destinatario. Non siamo responsabili per ritardi causati dalla dogana.",
        },
      ],
    },
    "de-DE": {
      title: "Versandrichtlinie",
      lead: "Wir verpflichten uns, deine Bestellungen schnell, sicher und zu einem fairen Preis zu liefern. Hier erfährst du alles Wichtige über unseren Versand.",
      sections: [
        {
          heading: "Auftragsbearbeitung",
          body: "Bestellungen werden innerhalb von 1–2 Werktagen nach Zahlungsbestätigung bearbeitet. Bestellungen, die an Wochenenden oder Feiertagen aufgegeben werden, werden am nächsten Werktag bearbeitet. Du erhältst eine Bestätigungs-E-Mail mit Sendungsverfolgung, sobald deine Bestellung versendet wurde.",
        },
        {
          heading: "Versandarten & Tarife",
          body: "Standardversand (3–5 Werktage) ist kostenlos bei Bestellungen über 50 € und kostet 4,99 € bei kleineren Bestellungen. Expressversand (1–2 Werktage) ist für 9,99 € verfügbar. Lieferung am selben Tag wird in ausgewählten Städten gegen Aufpreis angeboten.",
        },
        {
          heading: "Internationaler Versand",
          body: "Wir versenden in über 50 Länder weltweit. Die internationale Lieferung dauert in der Regel 7–14 Werktage. Einfuhrzölle und Steuern können bei der Ankunft anfallen und liegen in der Verantwortung des Empfängers. Wir haften nicht für durch den Zoll verursachte Verzögerungen.",
        },
      ],
    },
  },

  // ─────────────────────────── RETURNS POLICY ─────────────────────────────
  "returns-policy": {
    "en-US": {
      title: "Returns & Refunds Policy",
      lead: "Your satisfaction is our priority. If you are not fully satisfied with your purchase, we make it easy to return items and get a refund.",
      sections: [
        {
          heading: "Return Window & Conditions",
          body: "You may return any item within 30 days of receipt. Items must be unused, undamaged, and in their original packaging with all tags attached. Personalized, digital, and perishable products cannot be returned.",
        },
        {
          heading: "How to Return an Item",
          body: "Log into your account, navigate to your order history, and select the item you wish to return. You will receive a prepaid return label by email within 24 hours. Drop the package at any authorized carrier location.",
        },
        {
          heading: "Refunds",
          body: "Once we receive and inspect your return, we will issue a refund to your original payment method within 5–7 business days. You will receive an email confirmation when the refund is processed. Shipping costs are refunded only if the return is due to our error.",
        },
      ],
    },
    "fr-FR": {
      title: "Politique de Retours et Remboursements",
      lead: "Votre satisfaction est notre priorité. Si vous n'êtes pas entièrement satisfait de votre achat, nous facilitons le retour des articles et le remboursement.",
      sections: [
        {
          heading: "Délai et conditions de retour",
          body: "Vous pouvez retourner tout article dans les 30 jours suivant la réception. Les articles doivent être inutilisés, non endommagés et dans leur emballage d'origine avec toutes les étiquettes attachées. Les produits personnalisés, numériques et périssables ne peuvent pas être retournés.",
        },
        {
          heading: "Comment retourner un article",
          body: "Connectez-vous à votre compte, accédez à votre historique de commandes et sélectionnez l'article que vous souhaitez retourner. Vous recevrez une étiquette de retour prépayée par e-mail dans les 24 heures.",
        },
        {
          heading: "Remboursements",
          body: "Une fois que nous aurons reçu et inspecté votre retour, nous effectuerons un remboursement sur votre moyen de paiement d'origine dans un délai de 5 à 7 jours ouvrables. Les frais de livraison ne sont remboursés qu'en cas d'erreur de notre part.",
        },
      ],
    },
    "es-ES": {
      title: "Política de Devoluciones y Reembolsos",
      lead: "Tu satisfacción es nuestra prioridad. Si no estás completamente satisfecho con tu compra, hemos facilitado el proceso de devolución y reembolso.",
      sections: [
        {
          heading: "Plazo y condiciones de devolución",
          body: "Puedes devolver cualquier artículo dentro de los 30 días posteriores a la recepción. Los artículos deben estar sin usar, sin daños y en su embalaje original con todas las etiquetas puestas. Los productos personalizados, digitales y perecederos no pueden devolverse.",
        },
        {
          heading: "Cómo devolver un artículo",
          body: "Inicia sesión en tu cuenta, navega al historial de pedidos y selecciona el artículo que deseas devolver. Recibirás una etiqueta de devolución prepagada por email en 24 horas.",
        },
        {
          heading: "Reembolsos",
          body: "Una vez que recibamos e inspeccionemos tu devolución, emitiremos un reembolso en tu método de pago original en 5-7 días hábiles. Los costos de envío solo se reembolsan si la devolución se debe a un error nuestro.",
        },
      ],
    },
    "it-IT": {
      title: "Politica di Resi e Rimborsi",
      lead: "La tua soddisfazione è la nostra priorità. Se non sei completamente soddisfatto del tuo acquisto, rendiamo semplice restituire gli articoli e ottenere un rimborso.",
      sections: [
        {
          heading: "Finestra e condizioni di reso",
          body: "Puoi restituire qualsiasi articolo entro 30 giorni dal ricevimento. Gli articoli devono essere inutilizzati, non danneggiati e nella confezione originale con tutte le etichette attaccate. I prodotti personalizzati, digitali e deperibili non possono essere restituiti.",
        },
        {
          heading: "Come restituire un articolo",
          body: "Accedi al tuo account, vai alla cronologia degli ordini e seleziona l'articolo che desideri restituire. Riceverai un'etichetta di reso prepagata via email entro 24 ore. Consegna il pacco presso qualsiasi punto autorizzato del corriere.",
        },
        {
          heading: "Rimborsi",
          body: "Una volta ricevuto e ispezionato il tuo reso, emetteremo un rimborso sul tuo metodo di pagamento originale entro 5-7 giorni lavorativi. Riceverai un'email di conferma quando il rimborso viene elaborato. I costi di spedizione vengono rimborsati solo se il reso è dovuto a un nostro errore.",
        },
      ],
    },
    "de-DE": {
      title: "Rückgabe- und Erstattungsrichtlinie",
      lead: "Deine Zufriedenheit hat für uns Priorität. Falls du mit deinem Kauf nicht vollständig zufrieden bist, machen wir es dir leicht, Artikel zurückzusenden und eine Erstattung zu erhalten.",
      sections: [
        {
          heading: "Rückgabefrist & Bedingungen",
          body: "Du kannst jeden Artikel innerhalb von 30 Tagen nach Erhalt zurücksenden. Artikel müssen unbenutzt, unbeschädigt und in der Originalverpackung mit allen Etiketten sein. Personalisierte, digitale und verderbliche Produkte können nicht zurückgegeben werden.",
        },
        {
          heading: "So gibst du einen Artikel zurück",
          body: "Melde dich in deinem Konto an, gehe zu deiner Bestellhistorie und wähle den Artikel aus, den du zurücksenden möchtest. Du erhältst innerhalb von 24 Stunden ein vorfrankiertes Rücksendeetikett per E-Mail. Gib das Paket an einer autorisierten Versandstelle ab.",
        },
        {
          heading: "Erstattungen",
          body: "Sobald wir deine Rücksendung erhalten und geprüft haben, erstatten wir den Betrag innerhalb von 5–7 Werktagen auf deine ursprüngliche Zahlungsmethode. Du erhältst eine Bestätigungs-E-Mail, sobald die Erstattung bearbeitet wurde. Versandkosten werden nur erstattet, wenn die Rücksendung auf unseren Fehler zurückzuführen ist.",
        },
      ],
    },
  },

  // ─────────────────────────── PRIVACY POLICY ─────────────────────────────
  "privacy-policy": {
    "en-US": {
      title: "Privacy Policy",
      lead: "Your privacy matters to us. This policy explains what data we collect, why we collect it, and how you can control it. Last updated: January 1, 2026.",
      sections: [
        {
          heading: "Data We Collect",
          body: "We collect information you provide directly — such as your name, email address, shipping address, and payment details when placing an order. We also collect usage data automatically, including your IP address, browser type, pages visited, and the items you view or add to your cart.",
        },
        {
          heading: "How We Use Your Data",
          body: "We use your data to process orders, provide customer support, send transactional emails (order confirmations, shipping updates), and improve our platform. With your explicit consent, we may also send you marketing communications. We never sell your personal data to third parties.",
        },
        {
          heading: "Your Rights",
          body: "Under applicable data protection law (GDPR, CCPA), you have the right to access, correct, delete, or export your personal data. You may also withdraw consent for marketing at any time. To exercise your rights, contact us at store@canticadivina.com. We will respond within 30 days.",
        },
      ],
    },
    "fr-FR": {
      title: "Politique de confidentialité",
      lead: "Votre vie privée est importante pour nous. Cette politique explique quelles données nous collectons, pourquoi nous les collectons et comment vous pouvez les contrôler. Dernière mise à jour : 1er janvier 2026.",
      sections: [
        {
          heading: "Données que nous collectons",
          body: "Nous collectons les informations que vous fournissez directement — comme votre nom, adresse e-mail, adresse de livraison et coordonnées de paiement lors d'une commande. Nous collectons également des données d'utilisation automatiquement, notamment votre adresse IP, type de navigateur et pages visitées.",
        },
        {
          heading: "Comment nous utilisons vos données",
          body: "Nous utilisons vos données pour traiter les commandes, fournir un support client, envoyer des e-mails transactionnels et améliorer notre plateforme. Avec votre consentement explicite, nous pouvons également vous envoyer des communications marketing. Nous ne vendons jamais vos données personnelles à des tiers.",
        },
        {
          heading: "Vos droits",
          body: "En vertu du RGPD, vous avez le droit d'accéder, de corriger, de supprimer ou d'exporter vos données personnelles. Vous pouvez également retirer votre consentement aux communications marketing à tout moment. Pour exercer vos droits, contactez-nous à store@canticadivina.com.",
        },
      ],
    },
    "es-ES": {
      title: "Política de Privacidad",
      lead: "Tu privacidad nos importa. Esta política explica qué datos recopilamos, por qué los recopilamos y cómo puedes controlarlos. Última actualización: 1 de enero de 2026.",
      sections: [
        {
          heading: "Datos que recopilamos",
          body: "Recopilamos información que proporcionas directamente — como tu nombre, dirección de email, dirección de envío y datos de pago al realizar un pedido. También recopilamos datos de uso automáticamente, incluida tu dirección IP, tipo de navegador y páginas visitadas.",
        },
        {
          heading: "Cómo usamos tus datos",
          body: "Usamos tus datos para procesar pedidos, brindar soporte al cliente, enviar emails transaccionales y mejorar nuestra plataforma. Con tu consentimiento explícito, también podemos enviarte comunicaciones de marketing. Nunca vendemos tus datos personales a terceros.",
        },
        {
          heading: "Tus derechos",
          body: "Bajo el RGPD y las leyes de protección de datos aplicables, tienes derecho a acceder, corregir, eliminar o exportar tus datos personales. También puedes retirar el consentimiento para marketing en cualquier momento. Contáctanos en store@canticadivina.com.",
        },
      ],
    },
    "it-IT": {
      title: "Informativa sulla Privacy",
      lead: "La tua privacy è importante per noi. Questa informativa spiega quali dati raccogliamo, perché li raccogliamo e come puoi controllarli. Ultimo aggiornamento: 1 gennaio 2026.",
      sections: [
        {
          heading: "Dati che raccogliamo",
          body: "Raccogliamo le informazioni che ci fornisci direttamente — come nome, indirizzo email, indirizzo di spedizione e dettagli di pagamento quando effettui un ordine. Raccogliamo inoltre automaticamente dati di utilizzo, incluso il tuo indirizzo IP, tipo di browser, pagine visitate e gli articoli che visualizzi o aggiungi al carrello.",
        },
        {
          heading: "Come utilizziamo i tuoi dati",
          body: "Utilizziamo i tuoi dati per elaborare gli ordini, fornire assistenza clienti, inviare email transazionali (conferme d'ordine, aggiornamenti di spedizione) e migliorare la nostra piattaforma. Con il tuo consenso esplicito, potremmo anche inviarti comunicazioni di marketing. Non vendiamo mai i tuoi dati personali a terzi.",
        },
        {
          heading: "I tuoi diritti",
          body: "Ai sensi della normativa applicabile sulla protezione dei dati (GDPR, CCPA), hai il diritto di accedere, correggere, eliminare o esportare i tuoi dati personali. Puoi anche revocare il consenso al marketing in qualsiasi momento. Per esercitare i tuoi diritti, contattaci a store@canticadivina.com. Risponderemo entro 30 giorni.",
        },
      ],
    },
    "de-DE": {
      title: "Datenschutzerklärung",
      lead: "Deine Privatsphäre ist uns wichtig. Diese Richtlinie erklärt, welche Daten wir erheben, warum wir sie erheben und wie du sie kontrollieren kannst. Letzte Aktualisierung: 1. Januar 2026.",
      sections: [
        {
          heading: "Daten, die wir erheben",
          body: "Wir erheben Informationen, die du uns direkt zur Verfügung stellst — wie deinen Namen, deine E-Mail-Adresse, Lieferadresse und Zahlungsdetails bei einer Bestellung. Wir erheben außerdem automatisch Nutzungsdaten, einschließlich deiner IP-Adresse, deines Browsertyps, besuchter Seiten und der Artikel, die du ansiehst oder in den Warenkorb legst.",
        },
        {
          heading: "Wie wir deine Daten verwenden",
          body: "Wir verwenden deine Daten, um Bestellungen zu bearbeiten, Kundensupport zu leisten, transaktionsbezogene E-Mails (Bestellbestätigungen, Versand-Updates) zu senden und unsere Plattform zu verbessern. Mit deiner ausdrücklichen Zustimmung senden wir dir möglicherweise auch Marketingmitteilungen. Wir verkaufen deine personenbezogenen Daten niemals an Dritte.",
        },
        {
          heading: "Deine Rechte",
          body: "Gemäß geltendem Datenschutzrecht (DSGVO, CCPA) hast du das Recht, auf deine personenbezogenen Daten zuzugreifen, sie zu berichtigen, zu löschen oder zu exportieren. Du kannst deine Einwilligung zu Marketing jederzeit widerrufen. Um deine Rechte auszuüben, kontaktiere uns unter store@canticadivina.com. Wir antworten innerhalb von 30 Tagen.",
        },
      ],
    },
  },

  // ─────────────────────────── TERMS OF SERVICE ───────────────────────────
  "terms-of-service": {
    "en-US": {
      title: "Terms of Service",
      lead: "By accessing or using Cantica Divina, you agree to be bound by these Terms of Service. Please read them carefully. Last updated: January 1, 2026.",
      sections: [
        {
          heading: "Use of the Platform",
          body: "You may use our platform for lawful purposes only. You agree not to use Cantica Divina for any fraudulent activities, to transmit harmful or illegal content, or to violate any applicable laws. We reserve the right to terminate access for users who breach these terms.",
        },
        {
          heading: "Orders, Payments & Pricing",
          body: "All prices displayed are inclusive of applicable taxes unless otherwise stated. We reserve the right to modify prices at any time. An order is not confirmed until payment is successfully processed. We are not liable for pricing errors due to technical glitches, and we reserve the right to cancel any incorrectly priced orders.",
        },
        {
          heading: "Limitation of Liability",
          body: "To the fullest extent permitted by law, Cantica Divina shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount paid by you for the specific order giving rise to the claim.",
        },
      ],
    },
    "fr-FR": {
      title: "Conditions Générales de Vente",
      lead: "En accédant à Cantica Divina ou en l'utilisant, vous acceptez d'être lié par ces Conditions générales. Veuillez les lire attentivement. Dernière mise à jour : 1er janvier 2026.",
      sections: [
        {
          heading: "Utilisation de la plateforme",
          body: "Vous pouvez utiliser notre plateforme à des fins légales uniquement. Vous vous engagez à ne pas utiliser Cantica Divina pour des activités frauduleuses, transmettre du contenu nuisible ou illégal, ou violer les lois applicables. Nous nous réservons le droit de résilier l'accès des utilisateurs qui enfreignent ces conditions.",
        },
        {
          heading: "Commandes, paiements et tarification",
          body: "Tous les prix affichés incluent les taxes applicables sauf indication contraire. Nous nous réservons le droit de modifier les prix à tout moment. Une commande n'est confirmée que lorsque le paiement est traité avec succès. Nous nous réservons le droit d'annuler toute commande avec un prix incorrect.",
        },
        {
          heading: "Limitation de responsabilité",
          body: "Dans toute la mesure permise par la loi, Cantica Divina ne sera pas responsable des dommages indirects, accessoires, spéciaux ou consécutifs découlant de votre utilisation de la plateforme. Notre responsabilité totale ne dépassera pas le montant que vous avez payé pour votre commande.",
        },
      ],
    },
    "es-ES": {
      title: "Términos y Condiciones",
      lead: "Al acceder o usar Cantica Divina, aceptas estar sujeto a estos Términos de Servicio. Por favor léelos detenidamente. Última actualización: 1 de enero de 2026.",
      sections: [
        {
          heading: "Uso de la plataforma",
          body: "Solo puedes usar nuestra plataforma para fines legales. Aceptas no usar Cantica Divina para actividades fraudulentas, transmitir contenido dañino o ilegal, ni violar ninguna ley aplicable. Nos reservamos el derecho de cancelar el acceso a usuarios que incumplan estos términos.",
        },
        {
          heading: "Pedidos, pagos y precios",
          body: "Todos los precios mostrados incluyen los impuestos aplicables salvo que se indique lo contrario. Nos reservamos el derecho de modificar los precios en cualquier momento. Un pedido no está confirmado hasta que el pago se procesa correctamente.",
        },
        {
          heading: "Limitación de responsabilidad",
          body: "En la máxima medida permitida por la ley, Cantica Divina no será responsable de ningún daño indirecto, incidental, especial o consecuente que surja del uso de la plataforma. Nuestra responsabilidad total no excederá el importe pagado por tu pedido.",
        },
      ],
    },
    "it-IT": {
      title: "Termini di Servizio",
      lead: "Accedendo a Cantica Divina o utilizzandolo, accetti di essere vincolato da questi Termini di Servizio. Leggili attentamente. Ultimo aggiornamento: 1 gennaio 2026.",
      sections: [
        {
          heading: "Utilizzo della piattaforma",
          body: "Puoi utilizzare la nostra piattaforma solo per scopi legali. Accetti di non utilizzare Cantica Divina per attività fraudolente, di non trasmettere contenuti dannosi o illegali, o di violare leggi applicabili. Ci riserviamo il diritto di terminare l'accesso agli utenti che violano questi termini.",
        },
        {
          heading: "Ordini, pagamenti e prezzi",
          body: "Tutti i prezzi visualizzati includono le imposte applicabili salvo diversa indicazione. Ci riserviamo il diritto di modificare i prezzi in qualsiasi momento. Un ordine non è confermato finché il pagamento non viene elaborato con successo. Non siamo responsabili per errori di prezzo dovuti a problemi tecnici, e ci riserviamo il diritto di annullare ordini con prezzi errati.",
        },
        {
          heading: "Limitazione di responsabilità",
          body: "Nella misura massima consentita dalla legge, Cantica Divina non sarà responsabile per danni indiretti, incidentali, speciali o consequenziali derivanti dall'uso della piattaforma. La nostra responsabilità totale non supererà l'importo pagato per l'ordine specifico che ha dato origine al reclamo.",
        },
      ],
    },
    "de-DE": {
      title: "Nutzungsbedingungen",
      lead: "Durch den Zugriff auf oder die Nutzung von Cantica Divina stimmst du zu, an diese Nutzungsbedingungen gebunden zu sein. Bitte lies sie sorgfältig durch. Letzte Aktualisierung: 1. Januar 2026.",
      sections: [
        {
          heading: "Nutzung der Plattform",
          body: "Du darfst unsere Plattform nur für rechtmäßige Zwecke nutzen. Du stimmst zu, Cantica Divina nicht für betrügerische Aktivitäten zu nutzen, keine schädlichen oder illegalen Inhalte zu übertragen und keine geltenden Gesetze zu verletzen. Wir behalten uns das Recht vor, den Zugang für Nutzer zu beenden, die gegen diese Bedingungen verstoßen.",
        },
        {
          heading: "Bestellungen, Zahlungen & Preisgestaltung",
          body: "Alle angezeigten Preise verstehen sich inklusive geltender Steuern, sofern nicht anders angegeben. Wir behalten uns das Recht vor, Preise jederzeit zu ändern. Eine Bestellung ist erst bestätigt, wenn die Zahlung erfolgreich verarbeitet wurde. Wir haften nicht für Preisfehler aufgrund technischer Störungen und behalten uns das Recht vor, falsch bepreiste Bestellungen zu stornieren.",
        },
        {
          heading: "Haftungsbeschränkung",
          body: "Im gesetzlich zulässigen Umfang haftet Cantica Divina nicht für indirekte, zufällige, besondere oder Folgeschäden, die aus der Nutzung der Plattform entstehen. Unsere Gesamthaftung übersteigt nicht den von dir für die jeweilige Bestellung gezahlten Betrag.",
        },
      ],
    },
  },

  // ─────────────────────────── COOKIE POLICY ──────────────────────────────
  "cookie-policy": {
    "en-US": {
      title: "Cookie Policy",
      lead: "This Cookie Policy explains how Cantica Divina uses cookies and similar tracking technologies on our website. Last updated: January 1, 2026.",
      sections: [
        {
          heading: "What Are Cookies?",
          body: "Cookies are small text files placed on your device when you visit a website. They help the site remember your preferences, keep you logged in, and understand how you use the site. Cookies can be session-based (deleted when you close your browser) or persistent (stored for a set period).",
        },
        {
          heading: "Types of Cookies We Use",
          body: "We use strictly necessary cookies (essential for the site to function), functional cookies (remember your preferences like language and cart contents), analytics cookies (help us understand traffic and behavior using privacy-respecting tools), and marketing cookies (only with your explicit consent).",
        },
        {
          heading: "Managing Your Cookie Preferences",
          body: "You can manage or withdraw your consent at any time using the cookie settings panel accessible from the footer of any page. You may also configure your browser to block or delete cookies, though this may affect your experience on our site.",
        },
      ],
    },
    "fr-FR": {
      title: "Politique des Cookies",
      lead: "Cette Politique des Cookies explique comment Cantica Divina utilise les cookies et les technologies de suivi similaires sur notre site web. Dernière mise à jour : 1er janvier 2026.",
      sections: [
        {
          heading: "Qu'est-ce qu'un cookie ?",
          body: "Les cookies sont de petits fichiers texte placés sur votre appareil lorsque vous visitez un site web. Ils aident le site à mémoriser vos préférences, à vous maintenir connecté et à comprendre comment vous utilisez le site.",
        },
        {
          heading: "Types de cookies que nous utilisons",
          body: "Nous utilisons des cookies strictement nécessaires (essentiels au fonctionnement du site), des cookies fonctionnels (mémorisent vos préférences tels que la langue et le contenu du panier), des cookies analytiques et des cookies marketing (uniquement avec votre consentement explicite).",
        },
        {
          heading: "Gérer vos préférences",
          body: "Vous pouvez gérer ou retirer votre consentement à tout moment en utilisant le panneau de paramètres des cookies accessible depuis le pied de page. Vous pouvez également configurer votre navigateur pour bloquer ou supprimer les cookies.",
        },
      ],
    },
    "es-ES": {
      title: "Política de Cookies",
      lead: "Esta Política de Cookies explica cómo Cantica Divina usa cookies y tecnologías de seguimiento similares en nuestro sitio web. Última actualización: 1 de enero de 2026.",
      sections: [
        {
          heading: "¿Qué son las cookies?",
          body: "Las cookies son pequeños archivos de texto que se colocan en tu dispositivo cuando visitas un sitio web. Ayudan al sitio a recordar tus preferencias, mantenerte conectado y entender cómo usas el sitio.",
        },
        {
          heading: "Tipos de cookies que usamos",
          body: "Usamos cookies estrictamente necesarias (esenciales para el funcionamiento del sitio), cookies funcionales (recuerdan tus preferencias), cookies analíticas y cookies de marketing (solo con tu consentimiento explícito).",
        },
        {
          heading: "Gestionar tus preferencias",
          body: "Puedes gestionar o retirar tu consentimiento en cualquier momento usando el panel de configuración de cookies accesible desde el pie de página. También puedes configurar tu navegador para bloquear o eliminar cookies.",
        },
      ],
    },
    "it-IT": {
      title: "Cookie Policy",
      lead: "Questa Cookie Policy spiega come Cantica Divina utilizza i cookie e tecnologie di tracciamento simili sul nostro sito web. Ultimo aggiornamento: 1 gennaio 2026.",
      sections: [
        {
          heading: "Cosa sono i cookie?",
          body: "I cookie sono piccoli file di testo posizionati sul tuo dispositivo quando visiti un sito web. Aiutano il sito a ricordare le tue preferenze, a mantenerti connesso e a capire come utilizzi il sito. I cookie possono essere di sessione (eliminati alla chiusura del browser) o persistenti (memorizzati per un periodo definito).",
        },
        {
          heading: "Tipi di cookie che utilizziamo",
          body: "Utilizziamo cookie strettamente necessari (essenziali per il funzionamento del sito), cookie funzionali (ricordano le tue preferenze come lingua e contenuto del carrello), cookie analitici (ci aiutano a capire il traffico e il comportamento tramite strumenti che rispettano la privacy) e cookie di marketing (solo con il tuo consenso esplicito).",
        },
        {
          heading: "Gestire le tue preferenze sui cookie",
          body: "Puoi gestire o revocare il tuo consenso in qualsiasi momento utilizzando il pannello delle impostazioni cookie accessibile dal footer di qualsiasi pagina. Puoi anche configurare il tuo browser per bloccare o eliminare i cookie, sebbene questo possa influire sulla tua esperienza sul nostro sito.",
        },
      ],
    },
    "de-DE": {
      title: "Cookie-Richtlinie",
      lead: "Diese Cookie-Richtlinie erklärt, wie Cantica Divina Cookies und ähnliche Tracking-Technologien auf unserer Website verwendet. Letzte Aktualisierung: 1. Januar 2026.",
      sections: [
        {
          heading: "Was sind Cookies?",
          body: "Cookies sind kleine Textdateien, die beim Besuch einer Website auf deinem Gerät platziert werden. Sie helfen der Website, sich an deine Präferenzen zu erinnern, dich angemeldet zu halten und zu verstehen, wie du die Website nutzt. Cookies können sitzungsbasiert (beim Schließen des Browsers gelöscht) oder dauerhaft (für einen bestimmten Zeitraum gespeichert) sein.",
        },
        {
          heading: "Arten von Cookies, die wir verwenden",
          body: "Wir verwenden unbedingt erforderliche Cookies (für das Funktionieren der Website unerlässlich), funktionale Cookies (merken sich deine Präferenzen wie Sprache und Warenkorbinhalt), Analyse-Cookies (helfen uns, Traffic und Verhalten mit datenschutzfreundlichen Tools zu verstehen) und Marketing-Cookies (nur mit deiner ausdrücklichen Zustimmung).",
        },
        {
          heading: "Verwaltung deiner Cookie-Einstellungen",
          body: "Du kannst deine Zustimmung jederzeit über das Cookie-Einstellungsfeld verwalten oder widerrufen, das im Footer jeder Seite zugänglich ist. Du kannst auch deinen Browser so konfigurieren, dass Cookies blockiert oder gelöscht werden, was jedoch deine Erfahrung auf unserer Website beeinträchtigen kann.",
        },
      ],
    },
  },

  // ─────────────────────────── LEGAL MENTIONS ─────────────────────────────
  "legal-mentions": {
    "en-US": {
      title: "Legal Mentions",
      lead: "The following legal information applies to the Cantica Divina platform and its associated website. Last updated: January 1, 2026.",
      sections: [
        {
          heading: "Publisher Information",
          body: "Cantica Divina is operated by Cantica Divina, a company registered under applicable law. Registered address: available upon request. VAT registration number: available upon request. For any legal queries, contact: store@canticadivina.com.",
        },
        {
          heading: "Hosting",
          body: "This website and its associated services are hosted on Cloudflare's global network. Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. The platform leverages Cloudflare Workers and Durable Objects for a globally distributed, edge-native architecture.",
        },
        {
          heading: "Intellectual Property",
          body: "The Cantica Divina platform source code is published under the AGPL-3.0 license and is available at store@canticadivina.com (source available upon request, AGPL-3.0). All trademarks, logos, and brand assets remain the property of Cantica Divina. Unauthorized reproduction of these assets is prohibited.",
        },
      ],
    },
    "fr-FR": {
      title: "Mentions Légales",
      lead: "Les informations légales suivantes s'appliquent à la plateforme Cantica Divina et à son site web associé. Dernière mise à jour : 1er janvier 2026.",
      sections: [
        {
          heading: "Informations sur l'éditeur",
          body: "Cantica Divina est exploitée par Cantica Divina, société enregistrée conformément aux lois applicables. Adresse du siège social : disponible sur demande. Numéro de TVA intracommunautaire : disponible sur demande. Pour toute question juridique : store@canticadivina.com.",
        },
        {
          heading: "Hébergement",
          body: "Ce site web et ses services associés sont hébergés sur le réseau mondial de Cloudflare. Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, États-Unis. La plateforme utilise Cloudflare Workers et Durable Objects.",
        },
        {
          heading: "Propriété intellectuelle",
          body: "Le code source de la plateforme Cantica Divina est publié sous licence AGPL-3.0 et disponible sur store@canticadivina.com (source available upon request, AGPL-3.0). Toutes les marques, logos et ressources de marque restent la propriété de Cantica Divina. Leur reproduction non autorisée est interdite.",
        },
      ],
    },
    "es-ES": {
      title: "Aviso Legal",
      lead: "La siguiente información legal se aplica a la plataforma Cantica Divina y a su sitio web asociado. Última actualización: 1 de enero de 2026.",
      sections: [
        {
          heading: "Información del editor",
          body: "Cantica Divina es operada por Cantica Divina, empresa registrada bajo la legislación aplicable. Dirección registrada: disponible bajo solicitud. Para consultas legales: store@canticadivina.com.",
        },
        {
          heading: "Alojamiento",
          body: "Este sitio web y sus servicios asociados están alojados en la red global de Cloudflare. Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, EE.UU. La plataforma utiliza Cloudflare Workers y Durable Objects.",
        },
        {
          heading: "Propiedad intelectual",
          body: "El código fuente de Cantica Divina se publica bajo la licencia AGPL-3.0 y está disponible en store@canticadivina.com (source available upon request, AGPL-3.0). Todas las marcas, logotipos y activos de marca siguen siendo propiedad de Cantica Divina.",
        },
      ],
    },
    "it-IT": {
      title: "Note Legali",
      lead: "Le seguenti informazioni legali si applicano alla piattaforma Cantica Divina e al suo sito web associato. Ultimo aggiornamento: 1 gennaio 2026.",
      sections: [
        {
          heading: "Informazioni sull'editore",
          body: "Cantica Divina è gestita da Cantica Divina, società registrata secondo la legislazione applicabile. Indirizzo registrato: disponibile su richiesta. Numero di partita IVA: disponibile su richiesta. Per qualsiasi richiesta legale, contatta: store@canticadivina.com.",
        },
        {
          heading: "Hosting",
          body: "Questo sito web e i servizi ad esso associati sono ospitati sulla rete globale di Cloudflare. Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. La piattaforma sfrutta Cloudflare Workers e Durable Objects per un'architettura edge-native distribuita globalmente.",
        },
        {
          heading: "Proprietà intellettuale",
          body: "Il codice sorgente della piattaforma Cantica Divina è pubblicato sotto licenza AGPL-3.0 ed è disponibile scrivendo a store@canticadivina.com (source available upon request, AGPL-3.0). Tutti i marchi, i loghi e gli asset del brand rimangono di proprietà di Cantica Divina. La riproduzione non autorizzata di questi asset è vietata.",
        },
      ],
    },
    "de-DE": {
      title: "Impressum",
      lead: "Die folgenden rechtlichen Informationen gelten für die Cantica-Divina-Plattform und die dazugehörige Website. Letzte Aktualisierung: 1. Januar 2026.",
      sections: [
        {
          heading: "Angaben zum Betreiber",
          body: "Cantica Divina wird von Cantica Divina betrieben, einem nach geltendem Recht eingetragenen Unternehmen. Eingetragene Adresse: auf Anfrage verfügbar. Umsatzsteuer-Identifikationsnummer: auf Anfrage verfügbar. Für rechtliche Anfragen kontaktiere: store@canticadivina.com.",
        },
        {
          heading: "Hosting",
          body: "Diese Website und die dazugehörigen Dienste werden im globalen Netzwerk von Cloudflare gehostet. Cloudflare, Inc., 101 Townsend St, San Francisco, CA 94107, USA. Die Plattform nutzt Cloudflare Workers und Durable Objects für eine global verteilte, edge-native Architektur.",
        },
        {
          heading: "Geistiges Eigentum",
          body: "Der Quellcode der Cantica-Divina-Plattform wird unter der AGPL-3.0-Lizenz veröffentlicht und ist über store@canticadivina.com verfügbar (source available upon request, AGPL-3.0). Alle Marken, Logos und Markenmaterialien bleiben Eigentum von Cantica Divina. Die unbefugte Vervielfältigung dieser Materialien ist untersagt.",
        },
      ],
    },
  },
};

/**
 * Returns the page content for a given handle and locale,
 * falling back to en-US if the locale is not found.
 */
export function getCmsPage(
  handle: string,
  locale: string,
): CmsPageContent | null {
  const page = CMS_CONTENT[handle];

  if (!page) return null;

  // Try exact match, then language code only (e.g. "fr" from "fr-FR"), then en-US
  return (
    page[locale] ??
    page[
      Object.keys(page).find((k) => k.startsWith(locale.split("-")[0])) ?? ""
    ] ??
    page["en-US"] ??
    null
  );
}
